<template>
  <div class="orderLookup grid-y">
    <BreadcrumbWrapper class="margin-bottom-xs" :current-page="currentPage" />
    <h1 id="title" class="margin-bottom-m" :class="{ 'small-margin-bottom-s medium-margin-bottom-m': responseError }">
      {{ ORDER_LOOKUP.orderLookupHeading }}
    </h1>
    <Notification v-if="responseError" severity="error" :closable="false" class="margin-bottom-m">
      <!-- eslint-disable vue/no-v-html -->
      <div v-html="responseError"></div>
    </Notification>
    <FurnitureMattressStatus v-if="showFurnitureMattressOrderStatus" />
    <div v-else>
      <div v-if="!isSignedIn" class="margin-bottom-m grid-x">
        <h2 class="h4 cell margin-bottom-xxs">
          {{ ORDER_LOOKUP.viewPurchaseHistoryText }}
        </h2>
        <div class="cell small-margin-bottom-xs medium-margin-bottom-m">
          {{ ORDER_LOOKUP.signInMessage }}
        </div>
        <div class="cell medium-5">
          <button class="sign-in-hyperlink primary button button-small expanded" aria-label="Link to Sign In page" @click="setSignInCookie">
            {{ ORDER_LOOKUP.signInText }}
          </button>
        </div>
      </div>
      <div class="margin-bottom-m">
        <h2 class="h4 margin-bottom-xxs">
          {{ ORDER_LOOKUP.lookupAnOrderText }}
        </h2>
        <div class="show-for-large">
          {{ ORDER_LOOKUP.lookupOrderMessageForLarge }}
        </div>
        <div class="hide-for-large">
          {{ ORDER_LOOKUP.lookupOrderMessage }}
        </div>
      </div>
      <Tabs :tabs="tabs" :initial-tab="initialTab" class="cell tabs" @tab-click="tabClick">
        <template #v-tab-head-orderLookup>
          <div class="tab-head active">
            <span id="orderLookup">{{ ORDER_LOOKUP.searchOrderText }}</span>
          </div>
        </template>
        <template #v-tab-panel-orderLookup>
          <OrderLookup />
          <NeedHelp class="margin-top-m text-center align-center padding-top-m border-top" show-chat chat-class="order-lookup" />
        </template>

        <template #v-tab-head-furnitureDelivery>
          <div class="tab-head">
            <span id="furnitureLookup">{{ ORDER_LOOKUP.furnitureAndMattressDeliveryText }}</span>
          </div>
        </template>
        <template #v-tab-panel-furnitureDelivery>
          <FurnitureMattressDelivery />
          <NeedHelp class="text-center align-center padding-top-m border-top" show-chat chat-class="furniture-lookup" />
        </template>
      </Tabs>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Cookie, UserStatus } from '@vue-util/cookie';
import Tabs from '@atomic-ui/tabs/Tabs';
import Notification from '@atomic-ui/notification/Notification';
import { fireTag } from '@vue-util/tagManager';
import OrderLookup from '@/components/ppe/OrderLookup';
import FurnitureMattressDelivery from '@/components/ppe/lookup/FurnitureMattressDelivery';
import BreadcrumbWrapper from '@/components/common/BreadcrumbWrapper';
import FurnitureMattressStatus from '@/components/ppe/lookup/FurnitureMattressStatus';
import NeedHelp from '@/components/common/NeedHelp';
import { ORDER_LOOKUP } from 'config';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { useOrderDetailsStore } from '@/stores/useOrderDetailsStore';
import { useFurnitureStore } from '@/stores/ppe/useFurnitureStore';

const router = useRouter();

const envPropsStore = useEnvPropsStore();
const orderDetailsStore = useOrderDetailsStore();
const furnitureStore = useFurnitureStore();

const tabs = ref(['orderLookup', 'furnitureDelivery']);
const currentPage = ref('Order Lookup');
const isSignedIn = computed(() => UserStatus.isSignedIn());

const envProps = computed(() => envPropsStore.envProps);
const orderDetailsError = computed(() => orderDetailsStore.orderDetailsError);
const showFurnitureMattressOrderStatus = computed(() => {
  const status = furnitureStore.furnitureStatus;
  return !!(status !== undefined && Object.keys(status).length);
});
const responseError = computed(() => orderDetailsError.value);
const initialTab = computed(() =>
  window.location && new URLSearchParams(window.location.search).get('tab') === 'furniture' ? 'furnitureDelivery' : 'orderLookup',
);

onMounted(() => {
  fireTag('view', {
    page_name: ORDER_LOOKUP.orderLookupViewTag.page_name,
    page_type: ORDER_LOOKUP.orderLookupViewTag.page_type,
    customer_type: isSignedIn.value ? 'signed in' : 'guest',
  });
});

const getRedirectPath = () => {
  const routePath = router.resolve({ name: 'online-history' }).path;
  return `${window.location.origin}${routePath}`;
};

const setSignInCookie = () => {
  const domain = envProps.value.cookieDomain;
  Cookie.set('FORWARDPAGE_KEY', getRedirectPath(), null, { domain });
  window.location.href = '/account/signin';
};

const tabClick = (index) => {
  const tagForTab = index === 0 ? ORDER_LOOKUP.orderLookupViewTag : ORDER_LOOKUP.furnitureLookupViewTag;
  fireTag('view', {
    ...tagForTab,
  });
};
</script>
